import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { fmtUSD } from '@/lib/calc';
import { ArrowLeft, Flower2, Loader2, AlertCircle, RefreshCw, Archive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ProductDetail from '@/components/catalog/ProductDetail';

const WF_COLOR = {
  Active: 'bg-[hsl(var(--muted-green))] text-white border-[hsl(var(--muted-green-dark))]',
  Approved: 'bg-[hsl(var(--muted-green))]/25 text-[hsl(var(--muted-green-dark))] border-[hsl(var(--muted-green))]/50',
  Review: 'bg-[hsl(60_30%_88%)] text-foreground border-[hsl(60_20%_78%)]',
  Draft: 'bg-[hsl(var(--muted))] text-muted-foreground border-border',
  Archived: 'bg-foreground text-background border-foreground',
};

export default function CollectionDetail() {
  const { id } = useParams();
  const { role } = useAuth();
  const showPrice = ['admin','manager','sales','purchasing'].includes(role);
  const [state, setState] = useState('loading'); // loading | ok | error
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedSku, setSelectedSku] = useState(null);

  // Load only the collection summary + product summaries. The full recipe is
  // fetched lazily inside <ProductDetail /> when a tile is clicked.
  const load = useCallback(async () => {
    setState('loading'); setError(null);
    try {
      setData(await apiFetch(`/collections/${id}/detail`));
      setState('ok');
    } catch (e) {
      setError(e.message);
      setState('error');
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12 max-w-[1400px]" data-testid="collection-detail-page">
      <Link
        to="/collections"
        className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground mb-6 velvet-eyebrow"
        data-testid="collection-detail-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All collections
      </Link>

      {state === 'loading' && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-16 justify-center" data-testid="collection-detail-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading collection…
        </div>
      )}

      {state === 'error' && (
        <div className="velvet-card p-8 text-center max-w-md mx-auto" data-testid="collection-detail-error">
          <AlertCircle className="h-8 w-8 mx-auto text-destructive mb-3" />
          <p className="text-sm text-muted-foreground mb-4">{error || 'Could not load collection.'}</p>
          <Button onClick={load} className="rounded-sm" variant="outline" data-testid="collection-detail-retry">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
          </Button>
        </div>
      )}

      {state === 'ok' && data && (
        <>
          <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <div>
              <div className="velvet-eyebrow mb-3 flex items-center gap-3">
                <span>Collection</span>
                <span className="font-mono normal-case tracking-normal text-[10px]">{data.collection.code}</span>
                {data.collection.is_archived && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 border border-foreground bg-foreground text-background">
                    <Archive className="h-3 w-3" /> Archived
                  </span>
                )}
              </div>
              <h1 className="velvet-page-title" data-testid="collection-detail-title">{data.collection.name}</h1>
              {data.collection.description && (
                <p className="text-sm text-muted-foreground mt-3 max-w-2xl">{data.collection.description}</p>
              )}
            </div>
            <div className="text-right">
              <div className="velvet-eyebrow">Products</div>
              <div className="font-serif text-4xl mt-1" data-testid="collection-detail-count">{data.products.length}</div>
            </div>
          </header>

          {data.products.length === 0 ? (
            <div className="velvet-card p-12 text-center" data-testid="collection-detail-empty">
              <Flower2 className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">No products in this collection yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {data.products.map(p => (
                <button
                  key={p.id}
                  onClick={() => setSelectedSku(p.product_sku)}
                  className="text-left velvet-card overflow-hidden group hover:-translate-y-[2px] transition-transform"
                  data-testid={`coll-product-card-${p.product_sku}`}
                >
                  <div className="aspect-[4/3] bg-[hsl(var(--muted))] overflow-hidden relative">
                    {p.photo_url ? (
                      <img
                        src={p.photo_url}
                        alt={p.name}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display='none'; }}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground" data-testid={`coll-placeholder-${p.product_sku}`}>
                        <Flower2 className="h-8 w-8" />
                      </div>
                    )}
                    <span
                      className={`absolute top-3 left-3 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 border ${
                        p.is_draft
                          ? 'bg-[hsl(var(--muted))] text-muted-foreground border-border'
                          : p.active_for_sale
                            ? 'bg-[hsl(var(--muted-green))] text-white border-[hsl(var(--muted-green-dark))]'
                            : 'bg-foreground text-background border-foreground'
                      }`}
                      data-testid={`coll-status-${p.product_sku}`}
                    >
                      {p.is_draft ? 'Draft' : p.active_for_sale ? 'Active' : 'Inactive'}
                    </span>
                    {p.workflow_state && !p.is_draft && (
                      <span className={`absolute top-3 right-3 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 border ${WF_COLOR[p.workflow_state] || WF_COLOR.Draft}`}>
                        {p.workflow_state}
                      </span>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="font-serif text-2xl leading-tight tracking-tight">{p.name || 'Untitled'}</h3>
                      {showPrice && (
                        p.current_price != null ? (
                          <span className="velvet-money text-sm whitespace-nowrap">{fmtUSD(p.current_price)}</span>
                        ) : (
                          <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground whitespace-nowrap" data-testid={`coll-price-pending-${p.product_sku}`}>
                            Price pending
                          </span>
                        )
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                      <span className="font-mono">{p.product_sku}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      <ProductDetail sku={selectedSku} onClose={() => setSelectedSku(null)} onChanged={load} />
    </div>
  );
}
